const fixtureCache = new Map();

/**
 * Fetches a fixture JSON file from the static mock-data assets, caching the parsed
 * (mutable) object for the lifetime of the page session. Handlers may mutate the
 * cached object/array directly so later reads within the same session see the change.
 */
export function loadFixture(path) {
  if (!fixtureCache.has(path)) {
    const base = process.env.BASE_URL || '/';
    fixtureCache.set(
      path,
      fetch(`${base}mock-data/${path}.json`).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load mock fixture: ${path} (${response.status})`);
        }
        return response.json();
      }),
    );
  }
  return fixtureCache.get(path);
}

/** Clears every cached fixture. Exposed for tests; not used by app code. */
export function resetFixtureCache() {
  fixtureCache.clear();
}

/**
 * Returns the standard DRF paginated envelope for an in-memory array, honoring
 * `page` / `page_size` query params the same way the real backend does.
 */
export function paginate(list, { page, page_size: pageSize } = {}) {
  const size = Number(pageSize) || list.length || 1;
  const currentPage = Number(page) || 1;
  const start = (currentPage - 1) * size;
  const results = list.slice(start, start + size);

  return {
    count: list.length,
    next: start + size < list.length ? `page=${currentPage + 1}` : null,
    previous: currentPage > 1 ? `page=${currentPage - 1}` : null,
    results,
  };
}

export function findById(list, id) {
  return list.find((item) => item.id === id);
}

export function findBySlug(list, slug) {
  return list.find((item) => item.slug === slug);
}

const GROUP_IDENTIFIER_PATTERN = /\/groups\/([\w-]+)(?:\/|$)/;

/**
 * Extracts the group identifier from a group-scoped request URL. Different endpoints
 * key off different identifiers for the same group — most use the slug
 * (`/api/v1/groups/{slug}/profiles/`), but a few (e.g. media) use the numeric group id
 * (`/api/v1/groups/{id}/media/`). This just returns whatever token sits in that
 * position; callers pass it straight into `groupFixtureName`, which has entries for
 * both forms.
 */
export function groupIdentifierFromUrl(url) {
  const match = url.match(GROUP_IDENTIFIER_PATTERN);
  return match ? match[1] : undefined;
}

/**
 * The demo was originally scoped to a single group persona ("sunnydale-class-of-2005",
 * id 501) — see docs/plans/demo-environment-api-mocking.md — so its fixtures kept plain,
 * unsuffixed names (`groups/group`, `groups/profiles`, etc.). Additional demo groups get
 * their own suffixed fixture set here so navigating into a different group shows that
 * group's own content instead of falling back to the original persona's.
 */
const GROUP_FIXTURE_SUFFIX = {
  'sunnydale-alumni-network': 'alumni-network',
  502: 'alumni-network',
  'ellis-family-reunion': 'family-reunion',
  503: 'family-reunion',
};

/** Resolves a base fixture name (e.g. `groups/profiles`) to a group-specific variant. */
export function groupFixtureName(baseName, identifier) {
  const suffix = GROUP_FIXTURE_SUFFIX[identifier];
  return suffix ? `${baseName}-${suffix}` : baseName;
}

/**
 * Threaded post/comment shapes (FeedPost, EventDiscussionPost) nest one level of replies
 * under `childrenKey` (e.g. `sub_discussions`). These two helpers search/remove across
 * both the top level and that one level of nesting — matching the only depth the UI
 * (Post.vue / EventDiscussion.vue) ever actually reads.
 */
export function findInTree(list, id, childrenKey = 'sub_discussions') {
  for (const item of list) {
    if (item.id === id) return item;
    const found = (item[childrenKey] || []).find((child) => child.id === id);
    if (found) return found;
  }
  return undefined;
}

export function removeFromTree(list, id, childrenKey = 'sub_discussions') {
  const topIndex = list.findIndex((item) => item.id === id);
  if (topIndex !== -1) {
    list.splice(topIndex, 1);
    return true;
  }
  for (const item of list) {
    const children = item[childrenKey] || [];
    const childIndex = children.findIndex((child) => child.id === id);
    if (childIndex !== -1) {
      children.splice(childIndex, 1);
      return true;
    }
  }
  return false;
}

/**
 * The nested `profile` reference threaded posts/comments require on every entry (see
 * FeedPost/EventDiscussionPost in the schema doc). New posts/comments/likes created
 * through the demo UI are always authored by the logged-in demo persona, so this is a
 * fixed snapshot of `auth/current-user.json`'s identity fields rather than a fixture
 * lookup — duplicating those few fields here avoids an extra async fixture load on every
 * mutation for a value that never changes within a demo session.
 */
export const CURRENT_USER_PROFILE_REF = {
  id: 1001,
  slug: 'jordan-ellis',
  first_name: 'Jordan',
  last_name: 'Ellis',
  profile_picture: '/mock-data/assets/avatars/jordan-ellis.png',
};

/**
 * Merges a parsed request body onto an entity, converting any `Blob` values (a file
 * attached via `FormData`, e.g. an avatar or cover photo upload) into an object URL the
 * browser can actually render, instead of leaving a raw `Blob` on the entity.
 */
export function mergeEntity(target, body, blobFields = []) {
  Object.assign(target, body);
  blobFields.forEach((field) => {
    if (body[field] instanceof Blob) {
      target[field] = URL.createObjectURL(body[field]);
    }
  });
  return target;
}

export function nextId(list) {
  if (!list.length) return 1;
  return Math.max(...list.map((item) => item.id)) + 1;
}

/**
 * Extracts a regex capture group from a mocked request's `config.url`.
 *
 * `axios-mock-adapter`'s `.reply()` callback is only ever called with `config` — it does
 * NOT pass along the regex match array, even when the route was matched by a `RegExp`
 * with capture groups (verified against `node_modules/axios-mock-adapter/src/
 * handle_request.js`: `handler[3](config)`, no second argument). Route handlers that need
 * an id/slug out of the URL must re-run the same route regex themselves via this helper.
 */
export function urlParam(config, routeRegex, groupIndex = 1) {
  const match = config.url.match(routeRegex);
  return match ? match[groupIndex] : undefined;
}

/** Parses the query string off a mocked request's `config.url`. */
export function parseQuery(url) {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) return {};
  return Object.fromEntries(new URLSearchParams(url.slice(queryIndex + 1)));
}

/**
 * Normalizes a mocked request's body. By the time a request reaches the mock adapter,
 * axios's `transformRequest` chain has already run, so JSON bodies arrive as strings and
 * `FormData` bodies arrive as `FormData` instances (see `httpClient.js`) — this reads
 * either shape back into a plain object.
 */
export function parseBody(config) {
  const { data } = config;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    return Object.fromEntries(data.entries());
  }
  return data || {};
}
